import { useEffect, useState } from 'react';

/**
 * A React hook for tracking the previous value of a state or prop.
 * This hook allows you to access the value that a prop or state had in the previous render. It's particularly
 * useful in scenarios where you need to compare the current and previous values to implement certain behaviors,
 * such as triggering side effects when a value changes, or preventing unnecessary updates or re-renders.
 *
 * @template T - The type of the value being tracked. This ensures type safety by allowing the hook to work
 *               with any type of value (e.g., number, string, object, etc.).
 *
 * @param {T} value - The current value to track. This value is typically derived from props or state.
 * @returns {T | null} The previous value of the input parameter. During the first render, when there is no
 *                     previous value, this hook returns `null`.
 *
 * @example
 * const MyComponent = ({ count }) => {
 *   const prevCount = usePrevious(count);
 *   useEffect(() => {
 *     if (prevCount !== null && count !== prevCount) {
 *       console.log(`Count changed from ${prevCount} to ${count}`);
 *     }
 *   }, [count, prevCount]);
 *
 *   return <div>Current count: {count}</div>;
 * };
 */
export function usePrevious<T>(value: T): T | null {
  const [current, setCurrent] = useState(value);
  const [previous, setPrevious] = useState<T | null>(null);

  if (value !== current) {
    setPrevious(current);
    setCurrent(value);
  }

  return previous;
}

/**
 * Custom React hook to determine if an element should scroll based on its content size.
 * This hook utilizes a ResizeObserver to monitor changes in the size of a referenced div element, and determines
 * whether the content of the element is larger than the element itself, necessitating scrolling. It's particularly
 * useful for dynamically adjusting UI elements or functionality based on the presence of overflow content.
 *
 * @param {React.MutableRefObject<HTMLElement | null>} ref - A ref object pointing to the DOM element to be observed.
 * @returns {boolean} A stateful value indicating whether the observed element should enable scrolling to accommodate
 * its content.
 */
export const useShouldScroll = (
  ref: React.MutableRefObject<HTMLElement | null>
) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  useEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver((entries) => {
        const [entry] = entries;
        if (entry && !!ref.current) {
          const target = ref.current;
          const shouldScrollNow = target.offsetHeight < target.scrollHeight;
          setShouldScroll(shouldScrollNow);
        }
      });

      // Observe resizing on each child of div element to detect when scrollHeight changes
      const divElement = ref.current;
      for (const child of [].slice.call(divElement.children)) {
        observer.observe(child);
      }
      return () => observer.disconnect();
    }
  }, [ref]);
  return shouldScroll;
};
